import { InsertDriveFile, Science, TextSnippet } from '@mui/icons-material';
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Typography,
  Tooltip,
} from '@mui/material';

import { useParams } from 'react-router-dom';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import {
  ToastSeverity,
  confirmDialogState,
  dialogState,
  projectState,
  toastStateSelector,
  visitListState,
} from 'state';
import { DialogTypes, GeneralIds, ModelTypes } from 'types/types';
import { useTranslation } from 'react-i18next';
import { Delete, Edit } from '@mui/icons-material';
import { getDateTimeString } from 'utils';
import { query, remove } from 'operations';
import { FormTemplateType, Visit } from 'api/models';
import AddFormMenu from './AddFormMenu';
import ExpandableTableRow from './ExpandableTableRow';
import useHasAccess from 'hooks/useHasAccess';
import { Permission } from 'state/permissionState';
import { Box, useMediaQuery, useTheme } from '@mui/system';

type VisitsTableProps = {
  visits: Visit[];
  formTemplateTypes: FormTemplateType[];
  sampleTemplateTypes: FormTemplateType[];
  reportTemplateTypes: FormTemplateType[];
};

export default function VisitsTable({
  visits,
  formTemplateTypes,
  sampleTemplateTypes,
  reportTemplateTypes,
}: VisitsTableProps) {
  const canAddVisit = useHasAccess([Permission.WRITE_VISIT]);
  const canAddForm = useHasAccess([Permission.WRITE_FORM]);
  const isProjectAdmin = useHasAccess([Permission.WRITE_PROJECT]);
  const { t, i18n } = useTranslation();
  const openConfirmDialog = useSetRecoilState(confirmDialogState);
  const setToast = useSetRecoilState(toastStateSelector);
  const setVisitList = useSetRecoilState(visitListState);
  const { patientId } = useParams<GeneralIds>();
  const { selectedProject } = useRecoilValue(projectState);
  const openDialog = useSetRecoilState(dialogState);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const onConfirmDelete = (id: string) => {
    return async () => {
      try {
        await remove({
          type: ModelTypes.visit,
          ids: {
            id,
            patientId,
          },
        });

        const { data: visits } = await query<Visit[]>({
          type: ModelTypes.visit,
          ids: {
            patientId,
          },
        });
        setVisitList(visits);
        openConfirmDialog({ isOpen: false });

        setToast({
          isOpen: true,
          message: t('general.success.delete'),
          severity: ToastSeverity.success,
        });
      } catch (e: any) {
        const errorCode = e.response?.data?.error?.[0].code;
        setToast({
          isOpen: true,
          message: i18n.exists(`errors.${errorCode}`)
            ? t(`errors.${errorCode}`)
            : t('projectCreation.errors.deletionFailed'),
          severity: ToastSeverity.error,
        });
      }
    };
  };

  // have to spread the array otherwise recoil gives an error because sort is updating a read only atom
  const sortedVisits = [...visits].sort((a, b) => {
    return new Date(b.date).getTime() - new Date(a.date).getTime();
  });

  return (
    <TableContainer sx={{ mt: 1 }}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell></TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>
              {t('general.name')}
            </TableCell>
            {!isMobile && (
              <TableCell sx={{ fontWeight: 'bold' }}>
                {t('general.date')}
              </TableCell>
            )}
            {!isMobile && (
              <TableCell sx={{ fontWeight: 'bold' }}>
                {t('visits.updatedBy')}
              </TableCell>
            )}
            <TableCell align="right" sx={{ fontWeight: 'bold' }}>
              {t('general.actions')}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedVisits.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6}>
                <Typography align="center">{t('general.noData')}</Typography>
              </TableCell>
            </TableRow>
          ) : (
            sortedVisits.map((visit, visitInd) => (
              <ExpandableTableRow
                visitId={visit.id}
                formTemplateTypes={formTemplateTypes}
                sampleTemplateTypes={sampleTemplateTypes}
                reportTemplateTypes={reportTemplateTypes}
                key={`users-table-${visit.id}-row`}
                sx={{
                  '&:last-child td, &:last-child th': { border: 0 },
                  '&:hover': {
                    backgroundColor: theme => theme.palette.action.hover,
                    transition: 'background-color 0.4s',
                  },
                  cursor: 'pointer',
                }}
                isOpenByDefault={visitInd === 0}
              >
                <TableCell sx={{ fontWeight: 'bold' }}>
                  {`${visit.visitTemplate.name} (${visit.index}. ${t(
                    'visits.lowCapsVisit',
                  )})`}
                </TableCell>
                {!isMobile && (
                  <TableCell>{getDateTimeString(visit.date)}</TableCell>
                )}
                {!isMobile && (
                  <TableCell>{`${visit.slimUser?.lastName} ${visit.slimUser?.firstName}`}</TableCell>
                )}
                <TableCell align="right">
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'end',
                      pl: 3,
                      flexDirection: 'row',
                    }}
                  >
                    <IconButton
                      size="small"
                      disabled={!canAddVisit}
                      onClick={e => {
                        e.stopPropagation();
                        openDialog({
                          isOpen: true,
                          dialogType: DialogTypes.addVisit,
                          data: visit,
                        });
                      }}
                    >
                      <Tooltip title={t('general.edit')}>
                        <Edit fontSize="small" />
                      </Tooltip>
                    </IconButton>

                    {isProjectAdmin && (
                      <AddFormMenu
                        visit={visit}
                        formTemplateTypes={reportTemplateTypes}
                        disabled={!canAddForm}
                        variant={'report'}
                        icon={<TextSnippet />}
                      />
                    )}

                    <AddFormMenu
                      visit={visit}
                      formTemplateTypes={formTemplateTypes}
                      disabled={!canAddForm}
                      variant={'form'}
                      icon={<InsertDriveFile />}
                    />

                    {selectedProject?.configuration?.samplesEnabled && (
                      <AddFormMenu
                        visit={visit}
                        formTemplateTypes={sampleTemplateTypes}
                        disabled={!canAddForm}
                        variant={'sample'}
                        icon={<Science />}
                      />
                    )}
                    <IconButton
                      disabled={!canAddVisit}
                      size="small"
                      sx={{ ml: 1 }}
                      onClick={e => {
                        e.stopPropagation();
                        openConfirmDialog({
                          isOpen: true,
                          dialogType: DialogTypes.confirmDelete,
                          action: onConfirmDelete(visit.id),
                        });
                      }}
                    >
                      <Tooltip title={t('general.delete')}>
                        <Delete fontSize="small" />
                      </Tooltip>
                    </IconButton>
                  </Box>
                </TableCell>
              </ExpandableTableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
